import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reminderService } from '../../../services/reminderService';

const AppointmentDetails = ({ appointment, onUpdate, onDelete }) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!appointment) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 h-full flex flex-col items-center justify-center text-center">
        <Icon name="Calendar" size={48} className="text-muted-foreground mb-3" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          اختر موعداً
        </h3>
        <p className="text-sm text-muted-foreground">
          انقر على موعد في التقويم لعرض التفاصيل
        </p>
      </div>
    );
  }

  const handleConfirm = () => {
    onUpdate({ ...appointment, status: 'confirmed', reminderSent: true });
  };

  const handleCancel = () => {
    if (window.confirm('هل أنت متأكد من إلغاء هذا الموعد؟')) {
      onUpdate({ ...appointment, status: 'cancelled' });
    }
  };

  const handleDelete = () => {
    if (isConfirming) {
      onDelete(appointment?.id);
      setIsConfirming(false);
    } else {
      setIsConfirming(true);
      setTimeout(() => setIsConfirming(false), 3000);
    }
  };

  const handleSendReminder = async () => {
    const result = await reminderService?.sendReminder(appointment, 'whatsapp');
    
    if (result?.success) {
      onUpdate({ ...appointment, reminderSent: true });
    } else {
      alert('فشل إرسال التذكير. يرجى المحاولة مرة أخرى.');
    }
  };

  const statusConfig = {
    confirmed: { label: 'مؤكد', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle' },
    pending: { label: 'قيد الانتظار', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
    cancelled: { label: 'ملغي', color: 'text-error', bg: 'bg-error/10', icon: 'XCircle' }
  };

  const status = statusConfig?.[appointment?.status] || statusConfig?.pending;

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className={`p-4 ${status?.bg} border-b border-border`}>
        <div className="flex items-center gap-2">
          <Icon name={status?.icon} size={20} className={status?.color} />
          <span className={`text-sm font-semibold ${status?.color}`}>
            {status?.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Client Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="User" size={18} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              {appointment?.clientName}
            </h3>
          </div>
          <a
            href={`tel:${appointment?.clientPhone}`}
            className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            <Icon name="Phone" size={14} />
            {appointment?.clientPhone}
          </a>
        </div>

        {/* Property Info */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-start gap-2">
            <Icon name="MapPin" size={18} className="text-primary mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">
                {appointment?.propertyAddress}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                رقم العقار: {appointment?.propertyId}
              </div>
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div className="pt-3 border-t border-border space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{appointment?.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">
              {appointment?.time} ({appointment?.duration} دقيقة)
            </span>
          </div>
        </div>

        {/* Staff Info */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <Icon name="UserCheck" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{appointment?.staffName}</span>
          </div>
        </div>

        {/* Notes */}
        {appointment?.notes && (
          <div className="pt-3 border-t border-border">
            <div className="flex items-start gap-2">
              <Icon name="FileText" size={16} className="text-muted-foreground mt-0.5" />
              <p className="text-sm text-muted-foreground flex-1">
                {appointment?.notes}
              </p>
            </div>
          </div>
        )}

        {/* Reminder Status */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <Icon 
              name={appointment?.reminderSent ? 'CheckCircle' : 'Bell'} 
              size={16} 
              className={appointment?.reminderSent ? 'text-success' : 'text-muted-foreground'}
            />
            <span className="text-sm text-muted-foreground">
              {appointment?.reminderSent ? 'تم إرسال التذكير' : 'لم يتم إرسال التذكير'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-border space-y-2">
        {appointment?.status === 'pending' && (
          <Button
            variant="default"
            iconName="CheckCircle"
            fullWidth
            onClick={handleConfirm}
          >
            تأكيد الموعد
          </Button>
        )}

        {!appointment?.reminderSent && appointment?.status !== 'cancelled' && (
          <Button
            variant="outline"
            iconName="Send"
            fullWidth
            onClick={handleSendReminder}
          >
            إرسال تذكير
          </Button>
        )}

        {appointment?.status !== 'cancelled' && (
          <Button
            variant="outline"
            iconName="XCircle"
            fullWidth
            onClick={handleCancel}
          >
            إلغاء الموعد
          </Button>
        )}

        <Button
          variant={isConfirming ? 'danger' : 'ghost'}
          iconName="Trash2"
          fullWidth
          onClick={handleDelete}
        >
          {isConfirming ? 'انقر مرة أخرى للتأكيد' : 'حذف الموعد'}
        </Button>
      </div>
    </div>
  );
};

export default AppointmentDetails;